import {
  Avatar,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { getFirstLettersOfName, getRandomPastelColor } from '@/shared/services';
import { KnownAccount } from '../storage/types';

type AccountPickerProps = {
  accounts: KnownAccount[];
  onSelectAccount: (email: string) => void;
  onUseAnother: () => void;
  onRemoveAccount: (email: string) => void;
};

const AccountPicker = ({
  accounts,
  onSelectAccount,
  onUseAnother,
  onRemoveAccount,
}: AccountPickerProps) => {
  const getDisplayName = (account: KnownAccount) => {
    if (account.firstName || account.lastName) {
      return [account.firstName, account.lastName].filter(Boolean).join(' ');
    }
    return account.email;
  };

  const getSubtext = (account: KnownAccount) => {
    if (account.firstName || account.lastName) {
      return account.email;
    }
    return '';
  };

  return (
    <List sx={{ width: '100%', pt: 0 }}>
      {accounts.map((account) => {
        const initials = getFirstLettersOfName(account.email);
        const bgColor = getRandomPastelColor(account.email[0], account.email[2] ?? account.email[1]);
        const borderColor = '#4caf50';

        return (
          <ListItem
            key={account.email}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveAccount(account.email);
                }}
                sx={{ color: 'grey.500', '&:hover': { color: 'white' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <ListItemButton
              onClick={() => onSelectAccount(account.email)}
              sx={{ borderRadius: 1, py: 1.5 }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: bgColor,
                    border: `2px solid ${borderColor}`,
                    color: '#333',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {initials}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={getDisplayName(account)}
                secondary={getSubtext(account)}
                slotProps={{
                  primary: { sx: { color: 'white' } },
                  secondary: { sx: { color: 'grey.400', fontSize: '0.8rem' } },
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}

      <ListItem disablePadding sx={{ mt: 1 }}>
        <Button
          fullWidth
          startIcon={<PersonAddIcon />}
          onClick={onUseAnother}
          sx={{ color: 'white', justifyContent: 'flex-start', px: 2, py: 1.5 }}
        >
          <Typography variant="body2">Use another account</Typography>
        </Button>
      </ListItem>
    </List>
  );
};

export default AccountPicker;
